/**
 * Live-mode use cases. The browser builds the normal use cases (reads and plain edits run against RLS-protected tables), then this
 * wrapper replaces the operations listed in ops.ts with calls to the Edge Functions, which run the SAME use-case code with
 * service-side adapters (tokens, messaging, money). The function answers with the use case's own Result, so callers cannot tell.
 */
import type { SchedulerReport, UseCases } from '../../domain/services';
import type { AuthAdapter } from '../types';
import type { Rest } from './rest';
import { OPS, type RemoteOp } from './ops';

export interface LiveUseCaseOptions {
  /** Called after an owner/either operation finished, so views can re-read right away (the poller is the safety net). */
  afterOp?: (op: RemoteOp) => void;
  /** The browser session learns which business it now owns (the server linked it; the local use case would have called linkSession itself). */
  auth?: Pick<AuthAdapter, 'linkSession'>;
}

const NOTHING: Omit<SchedulerReport, 'at'> = { downgradedTrials: 0, accountsDeleted: 0, depositsConfirmed: 0, holdsReleased: 0, offersExpired: 0, suppressed: 0, deferred: 0, progressed: 0 };

export function createLiveUseCases(base: UseCases, rest: Rest, opts: LiveUseCaseOptions = {}): UseCases {
  const out: Record<string, unknown> = { ...base };
  for (const op of Object.keys(OPS) as RemoteOp[]) {
    const spec = OPS[op];
    out[op] = async (...args: unknown[]): Promise<unknown> => {
      // Public operations never send the login: an expired token would turn a harmless booking page view into a 401.
      const result = await rest.invoke(spec.fn, { op, args }, { auth: spec.access === 'public' ? 'anon' : 'user' });
      if (op === 'createOwnerAccount') {
        const r = result as { ok?: boolean; value?: { owner?: { id: string }; business?: { id: string } } };
        if (r.ok && r.value?.owner && r.value.business) opts.auth?.linkSession(r.value.owner.id, r.value.business.id);
      }
      if (spec.access !== 'public') opts.afterOp?.(op);
      return result;
    };
  }
  // The server scheduler (pg_cron -> scheduler function) owns this; the browser must not run reminders.
  out.runScheduledJobs = async (): Promise<SchedulerReport> => ({ at: new Date().toISOString(), ...NOTHING });
  return out as UseCases;
}
